import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, User } from "lucide-react";
import { PageSEO, ArticleSchema } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderInlineMarkdown = (text) => {
  const parts = [];
  let remaining = text;
  let keyIdx = 0;

  while (remaining.length > 0) {
    // Match markdown links [text](url)
    const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    // Match bold **text**
    const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);

    let firstMatch = null;
    let firstPos = remaining.length;

    if (linkMatch && linkMatch.index < firstPos) { firstMatch = "link"; firstPos = linkMatch.index; }
    if (boldMatch && boldMatch.index < firstPos) { firstMatch = "bold"; firstPos = boldMatch.index; }

    if (!firstMatch) {
      parts.push(remaining);
      break;
    }

    if (firstPos > 0) parts.push(remaining.slice(0, firstPos));

    if (firstMatch === "link") {
      parts.push(
        <a key={`lnk-${keyIdx++}`} href={linkMatch[2]} target="_blank" rel="noopener noreferrer"
          className="underline underline-offset-4 transition-colors hover:opacity-70"
          style={{ color: "var(--ll-green)", fontWeight: 500 }}>
          {linkMatch[1]}
        </a>
      );
      remaining = remaining.slice(firstPos + linkMatch[0].length);
    } else {
      parts.push(<strong key={`b-${keyIdx++}`} style={{ color: "var(--ll-text)", fontWeight: 600 }}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(firstPos + boldMatch[0].length);
    }
  }
  return parts;
};

const ContentBlock = ({ block }) => {
  if (block.startsWith("## ")) {
    return <h2 className="font-serif text-2xl sm:text-3xl font-medium mt-10 mb-4" style={{ color: "var(--ll-text)" }}>{block.replace("## ", "")}</h2>;
  }
  if (block.startsWith("### ")) {
    return <h3 className="font-serif text-xl font-medium mt-8 mb-3" style={{ color: "var(--ll-text)" }}>{block.replace("### ", "")}</h3>;
  }
  if (block.startsWith("> ")) {
    return (
      <blockquote className="my-8 pl-6 py-4" style={{ borderLeft: "3px solid var(--ll-accent)", background: "rgba(247,208,70,0.06)", borderRadius: "0 12px 12px 0" }}>
        <p className="text-base italic leading-[1.85]" style={{ color: "var(--ll-text)", fontFamily: "'Cormorant Garamond', serif" }}>
          {renderInlineMarkdown(block.replace(/^> /, ""))}
        </p>
      </blockquote>
    );
  }
  if (block.startsWith("— ") || block.startsWith("- ")) {
    const items = block.split("\n").filter(Boolean);
    return (
      <ul className="space-y-3 my-6 pl-1">
        {items.map((item, i) => (
          <li key={`li-${i}`} className="flex items-start gap-3">
            <span className="w-1.5 h-1.5 rounded-full mt-2.5 flex-shrink-0" style={{ background: "var(--ll-accent)" }} />
            <span className="text-base leading-[1.85]" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              {renderInlineMarkdown(item.replace(/^[—\-]\s*/, ""))}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className="text-base leading-[1.85] mb-4" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
      {renderInlineMarkdown(block)}
    </p>
  );
};

const BlogPostPage = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/blog/${postId}`);
        if (!cancelled) setPost(res.data.post);
      } catch {
        if (!cancelled) setPost(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPost();
    return () => { cancelled = true; };
  }, [postId]);

  if (loading) return (
    <main className="pt-20"><div className="py-32 text-center"><p className="text-sm" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Loading...</p></div></main>
  );

  if (!post) return (
    <main className="pt-20"><div className="py-32 text-center"><p className="text-sm" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>Post not found.</p></div></main>
  );

  const contentBlocks = post.content ? post.content.split("\n\n") : [];

  return (
    <main data-testid="blog-post-page" className="pt-20">
      <PageSEO
        title={post.title}
        description={post.seo?.meta_description || post.excerpt}
        path={`/blog/${post.id}`}
        type="article"
        image={post.hero_image}
      />
      <ArticleSchema post={post} />
      <article className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs mb-10 transition-colors hover:opacity-70" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
            <ArrowLeft size={12} /> Back to Blog
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs tracking-[0.15em] uppercase px-3 py-1 rounded-full" style={{ background: "var(--ll-bg-secondary)", color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
              {post.category}
            </span>
            <span className="flex items-center gap-1 text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
              <Clock size={12} /> {post.read_time}
            </span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.15] mb-6" style={{ color: "var(--ll-text)" }}>
            {post.title}
          </h1>

          <div className="flex items-center gap-3 mb-12 pb-8" style={{ borderBottom: "1px solid var(--ll-border)" }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--ll-bg-secondary)" }}>
              <User size={14} style={{ color: "var(--ll-text-muted)" }} />
            </div>
            <div>
              <p className="text-xs font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>{post.author}</p>
              <p className="text-[11px]" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
                {new Date(post.date).toLocaleDateString("en-ZA", { year: "numeric", month: "long", day: "numeric" })}
                {post.geo && ` · ${post.geo.city}, ${post.geo.region}`}
              </p>
            </div>
          </div>

          {post.hero_image && (
            <div className="mb-12 -mx-2 sm:-mx-4 md:-mx-8" data-testid="blog-hero-image">
              <img
                src={post.hero_image}
                alt={post.hero_image_alt || post.title}
                className="w-full rounded-2xl object-cover"
                style={{ maxHeight: "420px" }}
                loading="eager"
              />
            </div>
          )}

          <div data-testid="blog-post-content">
            {contentBlocks.length > 0
              ? contentBlocks.map((block, i) => <ContentBlock key={`block-${post.id}-${i}`} block={block} />)
              : <p className="text-sm" style={{ color: "var(--ll-text-muted)" }}>Full article coming soon.</p>
            }
          </div>

          <div className="mt-16 p-8 rounded-2xl text-center" style={{ background: "var(--ll-bg-secondary)", border: "1px solid var(--ll-border)" }}>
            <h3 className="font-serif text-xl font-medium mb-2" style={{ color: "var(--ll-text)" }}>Want to go deeper?</h3>
            <p className="text-sm mb-6" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>Download our free ebook and explore practical tools for your wellness journey.</p>
            <Link to="/#products" className="inline-flex rounded-full px-6 py-3 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5" style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}>Explore Our Products</Link>
          </div>
        </div>
      </article>
    </main>
  );
};

export default BlogPostPage;
